import { ColumnDef } from '@tanstack/react-table';
import { SourcingOpenings } from 'api-clients/monolith';
import _isEmpty from 'lodash/isEmpty';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import InformationCircle from '@/assets/icons/information.svg';
import { PerformanceStatusPill } from '@/components/base/performance-status-pill';
import { SourcingStatusPill } from '@/components/base/sourcing-status-pill';
import { Button } from '@/components/ui/button';
import { ChannelIcon } from '@/components/ui/channel-icon';
import { Checkbox } from '@/components/ui/checkbox';
import { ConversionStats } from '@/components/ui/conversion-stats';
import { CurrencyStats } from '@/components/ui/currency-stats';
import { Duration } from '@/components/ui/duration';
import { HireStats } from '@/components/ui/hire-stats';
import { OpeningStatus } from '@/components/ui/opening-status';
import { Tooltip } from '@/components/ui/tooltip';
import { Typography } from '@/components/ui/typography';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import { useNoHiringGoalDialog } from '@/contexts/no-hiring-goal-dialog-context';
import { useHireUrl } from '@/hooks/use-hire-url';
import { formatShortDate } from '@/utils/date-util';

import { ActionTakenNotifier } from './action-taken-notifier';
import { FlashAlert } from './flash-alert';
import { Menu } from './menu';
import { useAutomationFlashMessages } from './use-automation-flash-alerts';
import { useEditTargets } from './use-edit-targets';
import { DEFAULT_METRICS_FILTER_RANGE } from '@/hooks/use-metrics-filter-params';

export function useDashboardColumns(
  defaultMetricsPeriod?: number | null,
): ColumnDef<SourcingOpenings>[] {
  const { handleEditTargets } = useEditTargets();
  const { openNoHiringGoalDialog } = useNoHiringGoalDialog();
  const navigate = useNavigate();
  const { openJobPostingForm } = useJobPostingContext();

  const { getAutomationFlashMessages } = useAutomationFlashMessages();

  const handleNoHiringGoal = (rowItem: SourcingOpenings) => {
    return openNoHiringGoalDialog({
      title: rowItem.title,
      brandSlug: rowItem.brand_slug,
      locationId: rowItem.location_id,
      funnelId: rowItem.external_id,
    });
  };

  const handleTitleClick = (rowItem: SourcingOpenings) => {
    if (rowItem.hiring_goal) {
      return navigate(`${rowItem.slug}/channels`);
    }

    const queryParams = new URLSearchParams();
    queryParams.set(
      'filter_range',
      defaultMetricsPeriod?.toString() ?? DEFAULT_METRICS_FILTER_RANGE,
    );
    return navigate(`${rowItem.slug}/channels?${queryParams.toString()}`);
  };

  const handleTargetsClick = (rowItem: SourcingOpenings) => {
    if (!rowItem.hiring_goal) return handleNoHiringGoal(rowItem);

    return handleEditTargets(rowItem);
  };

  const handlePostJobClick = (opening: SourcingOpenings) => {
    openJobPostingForm({
      openings: [
        {
          id: opening.external_id,
          title: opening.title,
          active: opening.active,
          isSourcingEnabled: opening.is_sourcing_funnel,
        },
      ],
    });
  };

  const { getHireATHiringGoalUrl } = useHireUrl();

  const metricsPeriodDays = defaultMetricsPeriod ?? 30;
  const hiresApplicantsTooltipContent =
    defaultMetricsPeriod === null ? (
      <FormattedMessage
        defaultMessage="Based on hiring goal period or lifetime metrics if no goal set"
        description="Tooltip on Hires or Applicants column: reporting window when org default is lifetime"
      />
    ) : (
      <FormattedMessage
        defaultMessage="Based on hiring goal period or last {days} days if no goal set"
        values={{ days: metricsPeriodDays }}
        description="Tooltip on Hires or Applicants column: reporting window vs hiring goal; {days} is org default when no goal"
      />
    );
  const durationTooltipContent =
    defaultMetricsPeriod === null ? (
      <FormattedMessage
        defaultMessage="For openings without hiring goals, metrics are shown for their lifetime by default. You can change this in Settings."
        description="Tooltip on Duration column: lifetime default when org metrics period is lifetime"
      />
    ) : (
      <FormattedMessage
        defaultMessage="For openings without hiring goals, metrics are shown for the last {days} days by default. You can change this in Settings."
        values={{ days: metricsPeriodDays }}
        description="Tooltip on Duration column: default metrics window; {days} matches org setting"
      />
    );

  return [
    {
      id: 'select',
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected()
              ? true
              : table.getIsSomePageRowsSelected()
                ? 'indeterminate'
                : false
          }
          onCheckedChange={value => table.toggleAllPageRowsSelected(!!value)}
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={value => row.toggleSelected(!!value)}
        />
      ),
    },
    {
      accessorKey: 'title',
      header: () => (
        <FormattedMessage
          defaultMessage="Opening name"
          description="Dashboard table: job title column with link to opening"
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex max-w-[280px] min-w-[200px] items-center gap-1 overflow-hidden">
            <OpeningStatus active={row.original.active} />
            <Button
              variant="link"
              onClick={() => handleTitleClick(row.original)}
              className="h-auto text-left whitespace-break-spaces"
            >
              {row.original.title}
            </Button>
          </div>
        );
      },
    },
    {
      accessorKey: 'performance_status_metrics',
      header: () => (
        <FormattedMessage
          defaultMessage="Status"
          description="Dashboard row: on-track vs at-risk vs goal met for the hire period"
        />
      ),
      cell: ({ row }) => {
        const performanceStatusMetrics =
          row.original.performance_status_metrics;
        const { status, criteria } = performanceStatusMetrics;

        const actionTakenMetrics = row.original.at_risk_metrics;

        if (!status) return <span>-</span>;

        return (
          <div className="flex items-center gap-1">
            <PerformanceStatusPill
              status={status}
              criteria={criteria}
              expected={
                criteria === 'hire'
                  ? performanceStatusMetrics.expected_hires_count
                  : performanceStatusMetrics.expected_applicants_count
              }
              actual={
                criteria === 'hire'
                  ? row.original.hire_stats.actual
                  : row.original.applicants_stats.actual
              }
              startDate={
                row.original.hiring_goal?.start_date &&
                formatShortDate(row.original.hiring_goal?.start_date)
              }
              endDate={
                row.original.hiring_goal?.hire_by &&
                formatShortDate(row.original.hiring_goal?.hire_by)
              }
              duration={row.original.hiring_goal?.duration ?? undefined}
              manageHiringGoalLink={getHireATHiringGoalUrl(
                row.original.brand_slug ?? 'no_brand',
                row.original.location_id,
                row.original.external_id,
              )}
            />
            {actionTakenMetrics && (
              <ActionTakenNotifier metrics={actionTakenMetrics} />
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'hire_stats',
      header: () => (
        <span className="inline-flex max-w-full items-center gap-1 whitespace-nowrap">
          <FormattedMessage
            defaultMessage="Hires"
            description="Column: hires vs target for the opening’s goal"
          />
          <Tooltip content={hiresApplicantsTooltipContent}>
            <img src={InformationCircle} alt="" className="size-3" />
          </Tooltip>
        </span>
      ),
      cell: ({ row }) => (
        <HireStats
          stats={row.original.hire_stats}
          isHighlighted={row.original.has_cross_funnel_transitions}
          openingSlug={row.original.slug}
          hiringGoalUrl={
            row.original.hiring_goal
              ? undefined
              : getHireATHiringGoalUrl(
                  row.original.brand_slug ?? 'no_brand',
                  row.original.location_id,
                  row.original.external_id,
                )
          }
        />
      ),
    },
    {
      accessorKey: 'total_duration',
      header: () => (
        <span className="inline-flex max-w-full items-center gap-1 whitespace-nowrap">
          <FormattedMessage
            defaultMessage="Duration"
            description="Column: time left in the hire-by window"
          />
          <Tooltip content={durationTooltipContent}>
            <img src={InformationCircle} alt="" className="size-3" />
          </Tooltip>
        </span>
      ),
      cell: ({ row }) => {
        const automationMessageObj = row.original.flash_alert_messages.find(
          msg => msg.automation_type === 'hiring_goal_ended',
        );
        const automationMessage = getAutomationFlashMessages(
          automationMessageObj,
          row.original.duration_stats,
        );

        const status = row.original.performance_status_metrics?.status ?? null;
        const showDurationWarning =
          row.original.hiring_goal_percentage >= 90 && status !== 'goal_met';

        return (
          <div className="flex items-center gap-1">
            <Duration
              stats={row.original.duration_stats}
              brand={row.original.brand_slug ?? 'no_brand'}
              location={row.original.location_id}
              funnel={row.original.external_id}
              showMetricsPeriod={!row.original.hiring_goal}
              metricsPeriod={defaultMetricsPeriod}
              showNewGoalButton={
                Boolean(row.original.hiring_goal) &&
                !row.original.hiring_goal?.is_current
              }
              hiringGoalAutomationEnabled={
                !!automationMessageObj?.automation_type
              }
              showDurationWarning={showDurationWarning}
            />
            {automationMessage && (
              <FlashAlert description={automationMessage.description} />
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'conversion_stats',
      header: () => (
        <FormattedMessage
          defaultMessage="Conversion"
          description="Column: actual vs expected applicant-to-hire rate"
        />
      ),
      cell: ({ row }) => (
        <ConversionStats
          stats={row.original.conversion_stats}
          onClick={() => handleTargetsClick(row.original)}
          showOnlyActual={!row.original.hiring_goal}
        />
      ),
    },
    {
      accessorKey: 'applicants_stats',
      header: () => (
        <span className="inline-flex max-w-full items-center gap-1 whitespace-nowrap">
          <FormattedMessage
            defaultMessage="Applicants"
            description="Column: pipeline volume vs target applicants"
          />
          <Tooltip content={hiresApplicantsTooltipContent}>
            <img src={InformationCircle} alt="" className="size-3" />
          </Tooltip>
        </span>
      ),
      cell: ({ row }) => {
        const automationMessageObj = row.original.flash_alert_messages.find(
          msg => msg.automation_type === 'automated_as_goal_met',
        );
        const automationMessage =
          getAutomationFlashMessages(automationMessageObj);

        return (
          <div className="flex items-center gap-1">
            <HireStats
              stats={row.original.applicants_stats}
              openingSlug={row.original.slug}
              showOnlyActual={!row.original.hiring_goal}
            />
            {automationMessage && (
              <FlashAlert description={automationMessage.description} />
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'budget_stats',
      header: () => (
        <FormattedMessage
          defaultMessage="Budget spend"
          description="Column: dollars used vs target budget for the goal"
        />
      ),
      cell: ({ row }) => {
        const automationMessageObj = row.original.flash_alert_messages.find(
          msg => msg.automation_type === 'target_budget_met',
        );
        const automationMessage =
          getAutomationFlashMessages(automationMessageObj);

        return (
          <div className="flex items-center gap-1">
            <CurrencyStats
              stats={row.original.budget_stats}
              onClick={
                row.original.hiring_goal?.is_current
                  ? () => handleTargetsClick(row.original)
                  : undefined
              }
              isHighlighted={row.original.has_cross_funnel_spend}
              openingSlug={row.original.slug}
              showOnlyActual={!row.original.hiring_goal}
            />
            {automationMessage && (
              <FlashAlert description={automationMessage.description} />
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'total_cpa',
      header: () => (
        <FormattedMessage
          defaultMessage="CPA"
          description="Column: cost per applicant (acronym kept short for layout)"
        />
      ),
      cell: ({ row }) => (
        <CurrencyStats
          stats={row.original.cpa_stats}
          onClick={
            row.original.hiring_goal?.is_current
              ? () => handleTargetsClick(row.original)
              : undefined
          }
          showOnlyActual={!row.original.hiring_goal}
        />
      ),
    },
    {
      accessorKey: 'total_cph',
      header: () => (
        <FormattedMessage
          defaultMessage="CPH"
          description="Column: cost per hire (acronym kept short for layout)"
        />
      ),
      cell: ({ row }) => (
        <CurrencyStats
          stats={row.original.cph_stats}
          onClick={
            row.original.hiring_goal?.is_current
              ? () => handleTargetsClick(row.original)
              : undefined
          }
          showOnlyActual={!row.original.hiring_goal}
        />
      ),
    },
    {
      accessorKey: 'channels',
      header: () => (
        <FormattedMessage
          defaultMessage="Channels"
          description="Column: which paid boards this opening is on (icons only)"
        />
      ),
      cell: ({ row }) => {
        const { channels } = row.original;

        if (_isEmpty(channels)) return <span>-</span>;

        const visibleChannels = channels?.slice(0, 5);
        const overflow =
          (channels?.length ?? 0) - (visibleChannels?.length ?? 0);

        return (
          <div className="flex w-max items-center">
            {visibleChannels?.map(channel => (
              <ChannelIcon
                key={channel.slug}
                variant="small"
                channelSlug={channel.slug}
                channelName={channel.name}
                logoSquareUrl={channel.logo_square}
                logoRectangleUrl={channel.logo_rectangle}
                className="-mr-1 last:mr-0"
                isRounded={true}
              />
            ))}
            {overflow > 0 && (
              <span className="ml-2 text-xs text-gray-500">+{overflow}</span>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'sourcing_status',
      header: () => (
        <FormattedMessage
          defaultMessage="Sourcing"
          description="Column: org-wide sourcing status (on/off, automation, etc.)"
        />
      ),
      cell: ({ row }) => {
        const sourcingStatus = row.original.sourcing_status;

        if (!sourcingStatus) return <span>-</span>;

        return <SourcingStatusPill status={sourcingStatus} />;
      },
    },
    {
      accessorKey: 'brand_name',
      header: () => (
        <FormattedMessage
          defaultMessage="Brand"
          description="Column: employer brand for the requisition"
        />
      ),
      cell: ({ row }) => (
        <Typography variant="subtitle1">{row.original.brand_name}</Typography>
      ),
    },
    {
      accessorKey: 'menu',
      header: () => null,
      cell: ({ row }) => {
        return (
          <Menu
            opening={row.original}
            onEditTargets={() => handleTargetsClick(row.original)}
            onPostJob={() => handlePostJobClick(row.original)}
          />
        );
      },
    },
  ];
}
